import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoRequest = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    center_name: "",
    phone: "",
    num_children: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.center_name) {
      toast.error("Please fill in your name, email, and center name.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setDone(true);
      toast.success("Demo request sent! Our Ohio team will reach out within one business day.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" data-testid="demo-request-section" className="py-24 sm:py-32 bg-stone-900 text-stone-50 relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-emerald-300">
              Book a demo
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-medium text-stone-50 tracking-tight">
              See ChildCheck on your center's data in 15 minutes.
            </h2>
            <p className="mt-5 text-stone-400 leading-relaxed max-w-md">
              Tell us a bit about your center. An Ohio-based onboarding specialist will walk you through
              ratios, forms, and billing — using examples from centers like yours.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-stone-300">
              {[
                "Live OAC 5101:2-12-18 ratio walkthrough",
                "DCY 01234 e-sign demo with a sample family",
                "Billing + Stripe auto-pay in action",
                "Q&A with an Ohio childcare specialist",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="demo-success"
                className="rounded-3xl border border-emerald-700/50 bg-emerald-950/50 p-10 text-center"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-600 grid place-items-center">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-medium text-stone-50">
                  You are on our calendar.
                </h3>
                <p className="mt-3 text-stone-400 max-w-md mx-auto">
                  Look for an email from our Ohio onboarding team within one business day with available
                  time slots.
                </p>
                <Button
                  data-testid="demo-reset-btn"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", center_name: "", phone: "", num_children: "", message: "" });
                  }}
                  variant="outline"
                  className="mt-6 bg-transparent text-stone-50 border-stone-600 hover:bg-stone-800"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="demo-form"
                className="rounded-3xl border border-stone-700 bg-stone-950/60 backdrop-blur p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
              >
                <FormField label="Full name" required>
                  <Input
                    data-testid="demo-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Director"
                    className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500 h-11"
                  />
                </FormField>
                <FormField label="Work email" required>
                  <Input
                    data-testid="demo-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@yourcenter.com"
                    className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500 h-11"
                  />
                </FormField>
                <FormField label="Center name" required>
                  <Input
                    data-testid="demo-input-center"
                    value={form.center_name}
                    onChange={(e) => update("center_name", e.target.value)}
                    placeholder="Your center's name"
                    className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500 h-11"
                  />
                </FormField>
                <FormField label="Phone (optional)">
                  <Input
                    data-testid="demo-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(555) 000-0000"
                    className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500 h-11"
                  />
                </FormField>

                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-stone-400">Children enrolled</Label>
                  <Select value={form.num_children} onValueChange={(v) => update("num_children", v)}>
                    <SelectTrigger
                      data-testid="demo-input-children"
                      className="mt-2 bg-stone-900 border-stone-700 text-stone-50 h-11"
                    >
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-30">0 – 30</SelectItem>
                      <SelectItem value="31-75">31 – 75</SelectItem>
                      <SelectItem value="76-150">76 – 150</SelectItem>
                      <SelectItem value="151-300">151 – 300</SelectItem>
                      <SelectItem value="300+">300+</SelectItem>
                      <SelectItem value="multi">Multiple centers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <FormField label="What's on your mind?">
                    <Textarea
                      data-testid="demo-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Ohio licensing, billing, migration from another tool…"
                      rows={4}
                      className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500"
                    />
                  </FormField>
                </div>

                <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                  <div className="text-xs text-stone-500">
                    By submitting you agree to our terms. We never share your data.
                  </div>
                  <Button
                    type="submit"
                    data-testid="demo-submit-btn"
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold px-6 py-6 hover:-translate-y-0.5 transition-all"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Book my demo</>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, required, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-stone-400">
      {label} {required && <span className="text-orange-400">*</span>}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default DemoRequest;
